import classNames from "classnames";
import * as React from "react";
import useLoadingPresentation, { LoadingPresentation } from "../../../shared/hooks/useLoadingPresentation";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaOverflowText from "../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../ui-kit/typography/Text/Text";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import { LibraryItem, PackageVersion, VersionChangeRequest } from "./external-libraries.types";
import LibraryDetail from "./library-detail";
import LibraryEmptyState from "./library-empty-state";

interface LibraryRowProps {
  library: LibraryItem;
  onOpen: () => void;
  onDelete: (libraryId: string, libraryName: string) => void;
}

const LibraryRow: React.FC<LibraryRowProps> = ({ library, onOpen, onDelete }) => {
  const handleDelete = (e?: React.MouseEvent): void => {
    e?.stopPropagation();
    onDelete(library.id, library.name);
  };

  return (
    <div
      className="kuika-external-libraries__row kuika-external-libraries__row--package"
      data-testid="externalLibrariesInstalledRow"
    >
      <div
        className={`kuika-external-libraries__row-head ${OVERFLOW_REVEAL_HOST_CLASS}`}
        role="button"
        tabIndex={0}
        aria-label={`View ${library.name}`}
        onClick={onOpen}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
      >
        <KuikaSvg name="package" width={16} height={16} className="kuika-external-libraries__row-icon" />
        <div className="kuika-external-libraries__row-text">
          <KuikaTextMedium className="kuika-external-libraries__row-name">
            <KuikaOverflowText>{library.name}</KuikaOverflowText>
          </KuikaTextMedium>
          {library.description && (
            <KuikaSmallText className="kuika-external-libraries__row-subtitle">{library.description}</KuikaSmallText>
          )}
        </div>
        {library.version && (
          <span className="kuika-external-libraries__version-chip" data-testid="externalLibrariesInstalledRowVersion">
            {library.version}
          </span>
        )}
        <KuikaButton
          type="text"
          color="danger"
          size="sm"
          isIcon
          aria-label={`Remove ${library.name}`}
          className="kuika-external-libraries__row-delete"
          onClick={handleDelete}
        >
          <KuikaSvg name="trash" width={16} height={16} />
        </KuikaButton>
      </div>
    </div>
  );
};

interface VersionSwitcherProps {
  library: LibraryItem;
  versions: PackageVersion[];
  loading: boolean;
  submitting: boolean;
  onSelectVersion: (request: VersionChangeRequest) => void;
}

const VersionSwitcher: React.FC<VersionSwitcherProps> = ({
  library,
  versions,
  loading,
  submitting,
  onSelectVersion
}) => {
  const [target, setTarget] = React.useState<string | null>(null);

  // Clear the pending choice once the change lands (version changes) or a different library opens.
  React.useEffect(() => setTarget(null), [library.id, library.version]);

  const versionsPresentation: LoadingPresentation = useLoadingPresentation({
    isLoading: loading,
    hasData: versions.length > 0
  });

  if (target) {
    return (
      <div className="kuika-external-libraries__detail-section">
        <KuikaSmallText className="kuika-external-libraries__detail-section-label">Switch version</KuikaSmallText>
        <div className="kuika-external-libraries__confirm">
          <KuikaSmallText className="kuika-external-libraries__confirm-text">
            Change from <strong>{library.version}</strong> to <strong>{target}</strong>? This may affect your
            application.
          </KuikaSmallText>
          <div className="kuika-external-libraries__confirm-actions">
            <KuikaButton
              type="primary"
              color="danger"
              size="sm"
              loading={submitting}
              onClick={() =>
                onSelectVersion({
                  libraryId: library.id,
                  libraryName: library.name,
                  from: library.version,
                  to: target
                })
              }
            >
              Switch version
            </KuikaButton>
            <KuikaButton type="secondary" size="sm" disabled={submitting} onClick={() => setTarget(null)}>
              Cancel
            </KuikaButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="kuika-external-libraries__detail-section">
      <KuikaSmallText className="kuika-external-libraries__detail-section-label">Switch version</KuikaSmallText>
      {versionsPresentation === "skeleton" ? (
        <KuikaSkeletonRegion label="Loading versions" className="kuika-external-libraries__versions-loader">
          <KuikaSkeletonList rows={4} />
        </KuikaSkeletonRegion>
      ) : (
        <div className="kuika-external-libraries__versions-loader">
          {versions.map((entry) => {
            const isCurrent: boolean = entry.version === library.version;
            const choose = (): void => {
              if (!isCurrent) setTarget(entry.version);
            };
            return (
              <div
                key={entry.version}
                className={classNames("kuika-external-libraries__version", {
                  "kuika-external-libraries__version--current": isCurrent,
                  "kuika-external-libraries__version--selectable": !isCurrent
                })}
                role={isCurrent ? undefined : "button"}
                tabIndex={isCurrent ? undefined : 0}
                aria-label={isCurrent ? undefined : `Switch to ${entry.version}`}
                onClick={choose}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    choose();
                  }
                }}
              >
                <KuikaSvg name="check" width={16} height={16} className="kuika-external-libraries__version-check" />
                <KuikaText>{entry.version}</KuikaText>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface InstalledLibrariesListProps {
  libraries: LibraryItem[];
  loading: boolean;
  search: string;
  expanded: string | undefined;
  expandable: boolean;
  isModule: boolean;
  versions: PackageVersion[];
  loadingVersions: boolean;
  onExpand: (libraryName: string) => void;
  onDelete: (libraryId: string, libraryName: string) => void;
  onSelectVersion: (request: VersionChangeRequest) => void;
  onBrowse: () => void;
}

const InstalledLibrariesList: React.FC<InstalledLibrariesListProps> = ({
  libraries,
  loading,
  search,
  expanded,
  expandable,
  isModule,
  versions,
  loadingVersions,
  onExpand,
  onDelete,
  onSelectVersion,
  onBrowse
}) => {
  const [detailId, setDetailId] = React.useState<string | undefined>(undefined);
  const listPresentation: LoadingPresentation = useLoadingPresentation({
    isLoading: loading,
    hasData: libraries.length > 0
  });

  const openDetail = (library: LibraryItem): void => {
    setDetailId(library.id);
    // Registry libraries can switch versions — preload the list once (never toggles it off).
    if (expandable && expanded !== library.name) onExpand(library.name);
  };

  const detailLibrary: LibraryItem | undefined = detailId
    ? libraries.find((library) => library.id === detailId)
    : undefined;

  if (detailLibrary) {
    const action: React.ReactNode = (
      <KuikaButton
        type="secondary"
        color="danger"
        size="sm"
        loading={loading}
        onClick={() => onDelete(detailLibrary.id, detailLibrary.name)}
      >
        Remove
      </KuikaButton>
    );

    return (
      <LibraryDetail
        name={detailLibrary.name}
        version={detailLibrary.version}
        description={detailLibrary.description}
        action={action}
        onBack={() => setDetailId(undefined)}
      >
        {expandable && (
          <VersionSwitcher
            library={detailLibrary}
            versions={versions}
            loading={loadingVersions}
            submitting={loading}
            onSelectVersion={onSelectVersion}
          />
        )}
      </LibraryDetail>
    );
  }

  if (libraries.length === 0 && !loading) {
    if (search) {
      return (
        <LibraryEmptyState
          icon="search-off"
          title="No matches"
          description={`No installed ${isModule ? "modules" : "libraries"} match “${search}”.`}
        />
      );
    }
    return (
      <LibraryEmptyState
        icon="package"
        title={isModule ? "No modules yet" : "No libraries yet"}
        description={
          isModule
            ? "Add a module by its import path to use it in this datasource."
            : "Browse the registry to add a dependency to this datasource."
        }
        actionLabel={isModule ? "Add module" : "Browse registry"}
        onAction={onBrowse}
      />
    );
  }

  if (listPresentation === "skeleton") {
    return (
      <KuikaSkeletonRegion label="Loading libraries" className="kuika-external-libraries__loader">
        <KuikaSkeletonList rows={5} lines={2} />
      </KuikaSkeletonRegion>
    );
  }

  return (
    <div className="kuika-external-libraries__loader">
      {libraries.map((library) => (
        <LibraryRow
          key={`${library.id}-${library.version}`}
          library={library}
          onOpen={() => openDetail(library)}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

export default InstalledLibrariesList;
